import React, { useState } from 'react';
import { ArrowLeft, Baby, GraduationCap, Heart, Users, BookOpen, CheckCircle, Award, Play, ClipboardList, Send, Briefcase, Building2, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { infantToddlerInstitute } from '../data/programs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toast } from 'sonner';

const InfantToddlerInstitute = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    current_role: '',
    other_role: '',
    organization_name: '',
    organization_type: '',
    organization_address: '',
    years_in_ece: '',
    years_in_infant_toddler: '',
    certifications: [],
    other_certifications: '',
    coaching_experience: '',
    programs_supported: '',
    age_groups_focus: [],
    areas_of_expertise: [],
    professional_goals: '',
    why_join: '',
    supervisor_name: '',
    supervisor_email: ''
  });

  const roleOptions = [
    'Program Coach',
    'Technical Assistance Provider',
    'Infant/Toddler Specialist',
    'ECE Program Director',
    'ECE Coordinator/Administrator',
    'Professional Development Specialist',
    'Quality Improvement Specialist',
    'Family Child Care Network Coordinator',
    'Other'
  ];

  const organizationTypes = [
    'Child Care Center',
    'Family Child Care Network',
    'Head Start / Early Head Start',
    'Public Pre-K Program',
    'Community-Based Organization',
    'Government Agency (OSSE, DCPS, etc.)',
    'Higher Education Institution',
    'Non-Profit Organization',
    'Other'
  ];

  const yearsOptions = [
    'Less than 1 year',
    '1-2 years',
    '3-5 years',
    '6-10 years',
    '11-15 years',
    '16-20 years',
    'More than 20 years'
  ];

  const coachingExperienceOptions = [
    'No formal coaching experience',
    'Less than 1 year',
    '1-2 years',
    '3-5 years',
    '6-10 years',
    'More than 10 years'
  ];

  const certificationOptions = [
    'Child Development Associate (CDA)',
    'Infant/Toddler CDA',
    'Program for Infant/Toddler Care (PITC) Certificate',
    'Early Childhood Education Degree (AA/AS)',
    'Early Childhood Education Degree (BA/BS)',
    'Master\'s in ECE or Related Field',
    'DC Coaching Credential',
    'IACET Accredited Training',
    'Other Professional Certification'
  ];

  const ageGroupOptions = [
    'Infants (0-12 months)',
    'Toddlers (12-36 months)',
    'Both Infants and Toddlers'
  ];

  const expertiseAreas = [
    'Infant/Toddler Development',
    'Social-Emotional Development',
    'Language & Literacy Development',
    'Curriculum & Environment Design',
    'Family Engagement',
    'Reflective Practice & Coaching',
    'Quality Improvement (QRIS/Capital Quality)',
    'Inclusion & Special Needs',
    'Cultural Responsiveness',
    'Health, Safety & Nutrition',
    'Business Practices for ECE'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/itci/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          programs_supported: parseInt(formData.programs_supported) || 0,
          other_role: formData.current_role === 'Other' ? formData.other_role : null,
          other_certifications: formData.certifications.includes('Other Professional Certification') ? formData.other_certifications : null
        }),
      });

      if (!response.ok) {
        throw new Error('Registration failed');
      }

      setIsSubmitted(true);
      toast.success('Registration submitted successfully!', {
        description: 'We will review your application and contact you soon.'
      });
    } catch (error) {
      console.error('Error submitting registration:', error);
      toast.error('Failed to submit registration', {
        description: 'Please try again or contact us directly.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section id="main-content" role="main" tabIndex={-1} className="relative pt-20 pb-16 bg-gradient-to-br from-rose-600 via-pink-600 to-purple-700">
        <div className="absolute inset-0">
          <img
            src={infantToddlerInstitute.heroImage}
            alt={infantToddlerInstitute.title}
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-rose-900/80 to-purple-900/60" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16">
          <Link to="/" className="inline-flex items-center gap-2 text-rose-200 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          
          <div className="max-w-4xl">
            <span className="inline-block px-4 py-2 bg-white/20 text-white rounded-full text-sm font-semibold mb-6">
              DCELC Program
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              {infantToddlerInstitute.title}
            </h1>
            <p className="text-xl text-rose-100 mb-8">
              {infantToddlerInstitute.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Intro & Partnership */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-lg text-slate-700 leading-relaxed mb-8">
                {infantToddlerInstitute.intro}
              </p>
              <div className="bg-rose-50 rounded-xl p-6 border border-rose-100">
                <p className="text-sm text-rose-600 font-semibold mb-2">In Partnership With</p>
                <p className="text-xl font-bold text-slate-800">{infantToddlerInstitute.partnership}</p>
              </div>
            </div>
            <div className="space-y-4">
              {infantToddlerInstitute.achievements.map((achievement, index) => (
                <div key={index} className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                  <CheckCircle className="w-6 h-6 text-rose-500 flex-shrink-0 mt-0.5" />
                  <p className="text-slate-700">{achievement}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Goals Section */}
      <section className="py-16 bg-gradient-to-br from-rose-50 to-purple-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-rose-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <GraduationCap className="w-8 h-8 text-rose-600" />
          </div>
          <h2 className="text-3xl font-bold text-slate-800 mb-6">{infantToddlerInstitute.goals.title}</h2>
          <p className="text-xl text-slate-700">
            {infantToddlerInstitute.goals.description}
          </p>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-2xl shadow-lg">
              <p className="text-4xl font-bold text-rose-600 mb-2">200</p>
              <p className="text-slate-600">New Infant-Toddler Educators</p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-lg">
              <p className="text-4xl font-bold text-rose-600 mb-2">$850K</p>
              <p className="text-slate-600">Annual Workforce Scholarships</p>
            </div>
          </div>
        </div>
      </section>

      {/* Training Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Baby className="w-12 h-12 text-rose-500 mb-6" />
              <h2 className="text-3xl font-bold text-slate-800 mb-6">{infantToddlerInstitute.training.title}</h2>
              <p className="text-slate-700 leading-relaxed mb-6">
                {infantToddlerInstitute.training.description}
              </p>
              <p className="text-slate-600 italic bg-rose-50 p-4 rounded-xl border-l-4 border-rose-500">
                {infantToddlerInstitute.training.approach}
              </p>
            </div>
            <div className="bg-gradient-to-br from-rose-500 to-purple-600 rounded-2xl p-8 text-white">
              <h3 className="text-xl font-bold mb-6">Local ECE Leaders Will:</h3>
              <ul className="space-y-4">
                {infantToddlerInstitute.localLeaders.points.map((point, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-rose-200 flex-shrink-0 mt-0.5" />
                    <span className="text-rose-50">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Promo Video Section */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="w-16 h-16 bg-rose-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Play className="w-8 h-8 text-rose-400" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">Watch Our Promo Video</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Learn more about how the PITC model is transforming infant-toddler care in DC
            </p>
          </div>
          
          <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-black">
            <video
              controls
              className="w-full h-auto"
              preload="metadata"
            >
              <source 
                src="https://customer-assets.emergentagent.com/job_early-learn-dc/artifacts/ttwcnijg_DCELC_DCITC%20PROMO_FULL%20RECAP_FINAL%20CUT.mp4" 
                type="video/mp4" 
              />
              <track
                kind="captions"
                src="/captions/infant-toddler-institute.vtt"
                srcLang="en"
                label="English"
                default
              />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* Registration Form Section */}
      <section id="registration" className="py-20 bg-gradient-to-br from-rose-50 via-pink-50 to-purple-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <ClipboardList className="w-8 h-8 text-rose-600" />
            </div>
            <h2 className="text-3xl font-bold text-slate-800 mb-4">Apply to Join the Institute</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              This program is designed for program coaches, ECE leaders, technical assistance providers, 
              and infant/toddler specialists working across DC's mixed-delivery system.
            </p>
          </div>

          {isSubmitted ? (
            <div className="bg-white rounded-3xl p-12 shadow-xl border border-green-200 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-800 mb-4">Application Submitted!</h3>
              <p className="text-slate-600 mb-6">
                Thank you for applying to the Quality Improvement Institute for Infant and Toddler Care. 
                Our team will review your application and contact you within 5-7 business days.
              </p>
              <Button
                onClick={() => setIsSubmitted(false)}
                variant="outline"
                className="border-rose-300 text-rose-700 hover:bg-rose-50"
              >
                Submit Another Application
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-8 lg:p-12 shadow-xl border border-slate-200">
              
              {/* Personal Information */}
              <div className="mb-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-rose-100 rounded-xl flex items-center justify-center">
                    <Users className="w-5 h-5 text-rose-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800">Personal Information</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="first_name" className="text-slate-700 font-medium">First Name *</Label>
                    <Input
                      id="first_name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                      placeholder="Enter your first name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name" className="text-slate-700 font-medium">Last Name *</Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                      placeholder="Enter your last name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-slate-700 font-medium">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                      placeholder="you@example.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-slate-700 font-medium">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                      placeholder="(202) 555-0123"
                    />
                  </div>
                </div>
              </div>

              {/* Professional Role */}
              <div className="mb-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-pink-100 rounded-xl flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-pink-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800">Professional Role</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="current_role" className="text-slate-700 font-medium">Current Role *</Label>
                    <select
                      id="current_role"
                      name="current_role"
                      value={formData.current_role}
                      onChange={handleInputChange}
                      required
                      className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-slate-700 bg-white"
                    >
                      <option value="">Select your role</option>
                      {roleOptions.map((role) => (
                        <option key={role} value={role}>{role}</option>
                      ))}
                    </select>
                  </div>
                  {formData.current_role === 'Other' && (
                    <div>
                      <Label htmlFor="other_role" className="text-slate-700 font-medium">Please Specify *</Label>
                      <Input
                        id="other_role"
                        name="other_role"
                        value={formData.other_role}
                        onChange={handleInputChange}
                        required
                        className="mt-1"
                        placeholder="Your role"
                      />
                    </div>
                  )}
                  <div className="md:col-span-2">
                    <Label htmlFor="organization_name" className="text-slate-700 font-medium">Organization/Employer Name *</Label>
                    <Input
                      id="organization_name"
                      name="organization_name"
                      value={formData.organization_name}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                      placeholder="Enter your organization name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="organization_type" className="text-slate-700 font-medium">Organization Type *</Label>
                    <select
                      id="organization_type"
                      name="organization_type"
                      value={formData.organization_type}
                      onChange={handleInputChange}
                      required
                      className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-slate-700 bg-white"
                    >
                      <option value="">Select organization type</option>
                      {organizationTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="organization_address" className="text-slate-700 font-medium">Organization Address *</Label>
                    <Input
                      id="organization_address"
                      name="organization_address"
                      value={formData.organization_address}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                      placeholder="City, State"
                    />
                  </div>
                </div>
              </div>

              {/* Experience & Qualifications */}
              <div className="mb-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                    <GraduationCap className="w-5 h-5 text-purple-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800">Experience & Qualifications</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="years_in_ece" className="text-slate-700 font-medium">Years in Early Childhood Education *</Label>
                    <select
                      id="years_in_ece"
                      name="years_in_ece"
                      value={formData.years_in_ece}
                      onChange={handleInputChange}
                      required
                      className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-slate-700 bg-white"
                    >
                      <option value="">Select years</option>
                      {yearsOptions.map((years) => (
                        <option key={years} value={years}>{years}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="years_in_infant_toddler" className="text-slate-700 font-medium">Years Working with Infants/Toddlers *</Label>
                    <select
                      id="years_in_infant_toddler"
                      name="years_in_infant_toddler"
                      value={formData.years_in_infant_toddler}
                      onChange={handleInputChange}
                      required
                      className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-slate-700 bg-white"
                    >
                      <option value="">Select years</option>
                      {yearsOptions.map((years) => (
                        <option key={years} value={years}>{years}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-slate-700 font-medium mb-3 block">Certifications/Credentials (Select all that apply) *</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {certificationOptions.map((cert) => (
                        <label key={cert} className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-slate-50">
                          <input
                            type="checkbox"
                            checked={formData.certifications.includes(cert)}
                            onChange={() => handleCheckboxChange('certifications', cert)}
                            className="rounded border-slate-300 text-rose-600"
                          />
                          <span className="text-slate-700 text-sm">{cert}</span>
                        </label>
                      ))}
                    </div>
                    {formData.certifications.includes('Other Professional Certification') && (
                      <div className="mt-3">
                        <Input
                          name="other_certifications"
                          value={formData.other_certifications}
                          onChange={handleInputChange}
                          placeholder="Please specify other certification(s)"
                          className="mt-1"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Coaching/TA Experience */}
              <div className="mb-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-rose-100 rounded-xl flex items-center justify-center">
                    <Star className="w-5 h-5 text-rose-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800">Coaching & Technical Assistance Experience</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="coaching_experience" className="text-slate-700 font-medium">Coaching/TA Experience *</Label>
                    <select
                      id="coaching_experience"
                      name="coaching_experience"
                      value={formData.coaching_experience}
                      onChange={handleInputChange}
                      required
                      className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-slate-700 bg-white"
                    >
                      <option value="">Select experience level</option>
                      {coachingExperienceOptions.map((exp) => (
                        <option key={exp} value={exp}>{exp}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="programs_supported" className="text-slate-700 font-medium">Number of Programs Currently Supporting *</Label>
                    <Input
                      id="programs_supported"
                      name="programs_supported"
                      type="number"
                      min="0"
                      value={formData.programs_supported}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                      placeholder="e.g., 5"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-slate-700 font-medium mb-3 block">Age Groups You Work With *</Label>
                    <div className="flex flex-wrap gap-4">
                      {ageGroupOptions.map((age) => (
                        <label key={age} className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-slate-50">
                          <input
                            type="checkbox"
                            checked={formData.age_groups_focus.includes(age)}
                            onChange={() => handleCheckboxChange('age_groups_focus', age)}
                            className="rounded border-slate-300 text-rose-600"
                          />
                          <span className="text-slate-700">{age}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-slate-700 font-medium mb-3 block">Areas of Expertise (Select all that apply) *</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {expertiseAreas.map((area) => (
                        <label key={area} className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-slate-50">
                          <input
                            type="checkbox"
                            checked={formData.areas_of_expertise.includes(area)}
                            onChange={() => handleCheckboxChange('areas_of_expertise', area)}
                            className="rounded border-slate-300 text-rose-600"
                          />
                          <span className="text-slate-700 text-sm">{area}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Professional Goals */}
              <div className="mb-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-violet-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800">Professional Goals</h3>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="professional_goals" className="text-slate-700 font-medium">
                      What are your professional development goals? * <span className="font-normal text-slate-500">(150-300 words)</span>
                    </Label>
                    <p className="text-slate-500 text-sm mb-2">
                      Describe your goals related to infant/toddler coaching and technical assistance.
                    </p>
                    <Textarea
                      id="professional_goals"
                      name="professional_goals"
                      value={formData.professional_goals}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                      rows={4}
                      placeholder="Share your professional development goals..."
                    />
                  </div>
                  <div>
                    <Label htmlFor="why_join" className="text-slate-700 font-medium">
                      Why do you want to join the Quality Improvement Institute for Infant and Toddler Care? * <span className="font-normal text-slate-500">(200-400 words)</span>
                    </Label>
                    <p className="text-slate-500 text-sm mb-2">
                      Explain how this program aligns with your work and how you plan to apply what you learn.
                    </p>
                    <Textarea
                      id="why_join"
                      name="why_join"
                      value={formData.why_join}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                      rows={5}
                      placeholder="Tell us why you want to join the Institute..."
                    />
                  </div>
                </div>
              </div>

              {/* Supervisor Information */}
              <div className="mb-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-slate-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800">Supervisor Information (Optional)</h3>
                </div>
                
                <p className="text-slate-500 text-sm mb-4">
                  Provide your supervisor's contact information if you would like us to notify them of your application.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="supervisor_name" className="text-slate-700 font-medium">Supervisor Name</Label>
                    <Input
                      id="supervisor_name"
                      name="supervisor_name"
                      value={formData.supervisor_name}
                      onChange={handleInputChange}
                      className="mt-1"
                      placeholder="Supervisor's full name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="supervisor_email" className="text-slate-700 font-medium">Supervisor Email</Label>
                    <Input
                      id="supervisor_email"
                      name="supervisor_email"
                      type="email"
                      value={formData.supervisor_email}
                      onChange={handleInputChange}
                      className="mt-1"
                      placeholder="supervisor@example.com"
                    />
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="border-t border-slate-200 pt-8">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-rose-500 to-purple-600 hover:from-rose-600 hover:to-purple-700 text-white py-6 text-lg font-semibold disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Submitting Application...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Submit Application
                    </>
                  )}
                </Button>
                <p className="text-slate-500 text-sm text-center mt-4">
                  By submitting this application, you agree to be contacted by DCELC regarding the Quality Improvement Institute for Infant and Toddler Care.
                </p>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-slate-800 mb-6">Questions About the Institute?</h2>
          <p className="text-lg text-slate-600 mb-8">
            Contact us to learn more about the program and application process
          </p>
          <Button
            onClick={() => window.location.href = '/#contact'}
            variant="outline"
            className="border-rose-300 text-rose-700 hover:bg-rose-50 px-8 py-6 text-lg"
          >
            Contact Us
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InfantToddlerInstitute;
